/**
 * The suggestions inbox: pairs the server thinks may be one person, made
 * when a submission arrived sharing an email or phone with someone else and
 * the organisation links by suggestion rather than automatically.
 *
 * Link merges the newer person into the older; Not them keeps them apart and
 * stops the pair being suggested again. Both work on a ticked selection too.
 * Linking needs link_people; anyone else who sees people can read the inbox.
 */
import { useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Checkbox, Group, Loader, Pagination, SegmentedControl, Stack, Table, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { Link, Navigate } from 'react-router-dom';
import { useDecideSuggestions, useSuggestions, useUndoLink, type IdentitySuggestion } from '../../api/people';
import { formatNumber } from '../../theme';
import { PageHeader } from '../../ui';
import { relativeTime } from '../../ui/format';
import { useVocab } from '../../vocab';
import { useCrmPermissions } from '../crm/crmShared';
import { notifyWithUndo, PersonBriefView, ruleLabel } from './peopleShared';

const PAGE_SIZE = 50;
const STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'accepted', label: 'Linked' },
  { value: 'dismissed', label: 'Not them' },
  { value: 'all', label: 'All' },
];

const STATUS_BADGE: Record<string, { label: string; colour: string }> = {
  open: { label: 'Open', colour: 'chartAmber' },
  accepted: { label: 'Linked', colour: 'chartGreen' },
  dismissed: { label: 'Not them', colour: 'gray' },
};

export function SuggestionsPage() {
  const v = useVocab();
  const perms = useCrmPermissions();
  const [status, setStatus] = useState('open');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const list = useSuggestions({ status, page, page_size: PAGE_SIZE }, perms.peopleLinking);
  const decide = useDecideSuggestions();
  const undo = useUndoLink();

  if (!perms.peopleLinking) return <Navigate to="/" replace />;

  const rows = list.data?.suggestions ?? [];
  const openRows = rows.filter((s) => s.status === 'open');
  const canAct = perms.linkPeople;
  const allTicked = openRows.length > 0 && openRows.every((s) => selected.has(s.id));

  async function act(ids: string[], action: 'accept' | 'dismiss') {
    if (ids.length === 0) return;
    try {
      const r = await decide.mutateAsync({ ids, action });
      setSelected(new Set());
      const skipped = r.skipped > 0 ? ` ${formatNumber(r.skipped)} already decided.` : '';
      if (action === 'dismiss') {
        notifications.show({
          color: 'teal',
          title: 'Kept apart',
          message: `${formatNumber(r.dismissed)} not suggested again.${skipped}`,
        });
        return;
      }
      if (r.link_ids.length === 0) {
        notifications.show({ color: 'gray', title: 'Nothing linked', message: skipped.trim() || 'Nothing to link.' });
        return;
      }
      notifyWithUndo({
        title: 'Linked',
        message: `${formatNumber(r.accepted)} ${r.accepted === 1 ? 'pair' : 'pairs'} merged.${skipped}`,
        undo: async () => {
          try {
            for (const id of r.link_ids) await undo.mutateAsync(id);
            notifications.show({ color: 'teal', title: 'Undone', message: 'They are apart again.' });
          } catch (e) {
            notifications.show({ color: 'red', title: 'Not undone', message: (e as Error).message });
          }
        },
      });
    } catch (e) {
      notifications.show({ color: 'red', title: action === 'accept' ? 'Not linked' : 'Not changed', message: (e as Error).message });
    }
  }

  function toggle(id: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  const ticked = [...selected].filter((id) => openRows.some((s) => s.id === id));

  return (
    <Stack gap="md">
      <Anchor component={Link} to="/people" size="sm" c="dimmed">
        <Group gap={4}><IconArrowLeft size={14} />{v.Many('person')}</Group>
      </Anchor>
      <PageHeader
        title="Suggestions"
        description={`Pairs that may be the same ${v.one('person')}. Link merges the newer into the older; Not them keeps them apart for good.`}
      />

      <Group justify="space-between" wrap="wrap" gap="sm">
        <SegmentedControl
          size="xs"
          value={status}
          onChange={(s) => { setStatus(s); setPage(1); setSelected(new Set()); }}
          data={STATUSES}
          aria-label="Status"
        />
        {canAct && (
          <Group gap="xs">
            <Button
              size="xs"
              disabled={ticked.length === 0}
              loading={decide.isPending && decide.variables?.action === 'accept'}
              onClick={() => act(ticked, 'accept')}
            >
              {`Link selected${ticked.length ? ` (${ticked.length})` : ''}`}
            </Button>
            <Button
              size="xs"
              variant="default"
              disabled={ticked.length === 0}
              loading={decide.isPending && decide.variables?.action === 'dismiss'}
              onClick={() => act(ticked, 'dismiss')}
            >
              {`Not them${ticked.length ? ` (${ticked.length})` : ''}`}
            </Button>
          </Group>
        )}
      </Group>

      {list.error && <Alert color="red" variant="light">{list.error.message}</Alert>}

      <Card p={0}>
        {list.isPending ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing={10} data-testid="suggestions-table">
              <Table.Thead>
                <Table.Tr>
                  {canAct && (
                    <Table.Th w={36}>
                      <Checkbox
                        size="xs"
                        aria-label="Select all"
                        checked={allTicked}
                        indeterminate={!allTicked && ticked.length > 0}
                        disabled={openRows.length === 0}
                        onChange={(e) => setSelected(e.currentTarget.checked ? new Set(openRows.map((s) => s.id)) : new Set())}
                      />
                    </Table.Th>
                  )}
                  <Table.Th>{`New ${v.one('person')}`}</Table.Th>
                  <Table.Th>May be</Table.Th>
                  <Table.Th>Because</Table.Th>
                  <Table.Th>When</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 && (
                  <Table.Tr><Table.Td colSpan={canAct ? 6 : 5}>
                    <Text ta="center" c="dimmed" py="lg">
                      {status === 'open' ? 'Nothing waiting. New pairs show up here as they arrive.' : 'None.'}
                    </Text>
                  </Table.Td></Table.Tr>
                )}
                {rows.map((s) => (
                  <Row
                    key={s.id}
                    s={s}
                    canAct={canAct}
                    checked={selected.has(s.id)}
                    onCheck={(on) => toggle(s.id, on)}
                    onAct={(action) => act([s.id], action)}
                    busy={decide.isPending}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {(list.data?.pagination.total_pages ?? 0) > 1 && (
          <Group justify="flex-end" p="sm">
            <Pagination size="sm" total={list.data?.pagination.total_pages ?? 1} value={page} onChange={setPage} />
          </Group>
        )}
      </Card>
    </Stack>
  );
}

function Row({
  s, canAct, checked, onCheck, onAct, busy,
}: {
  s: IdentitySuggestion;
  canAct: boolean;
  checked: boolean;
  onCheck: (on: boolean) => void;
  onAct: (action: 'accept' | 'dismiss') => void;
  busy: boolean;
}) {
  const v = useVocab();
  const open = s.status === 'open';
  const badge = STATUS_BADGE[s.status];
  return (
    <Table.Tr data-testid={`suggestion-${s.id}`}>
      {canAct && (
        <Table.Td>
          {open && (
            <Checkbox size="xs" aria-label={`Select ${s.person.name || s.person.email}`} checked={checked} onChange={(e) => onCheck(e.currentTarget.checked)} />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Anchor component={Link} to={`/people/${encodeURIComponent(s.person.id)}`} c="inherit">
          <PersonBriefView person={s.person} v={v} />
        </Anchor>
      </Table.Td>
      <Table.Td>
        <Anchor component={Link} to={`/people/${encodeURIComponent(s.candidate.id)}`} c="inherit">
          <PersonBriefView person={s.candidate} v={v} />
        </Anchor>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color="chartAmber" radius="sm" tt="none">{ruleLabel(s.rule)}</Badge>
      </Table.Td>
      <Table.Td><Text size="sm" c="dimmed">{relativeTime(s.created_at)}</Text></Table.Td>
      <Table.Td ta="right">
        {open && canAct ? (
          <Group gap="xs" justify="flex-end" wrap="nowrap">
            <Button size="compact-sm" onClick={() => onAct('accept')} disabled={busy}>Link</Button>
            <Button size="compact-sm" variant="default" onClick={() => onAct('dismiss')} disabled={busy}>Not them</Button>
          </Group>
        ) : badge ? (
          <Badge variant="light" color={badge.colour} radius="sm" tt="none">{badge.label}</Badge>
        ) : null}
      </Table.Td>
    </Table.Tr>
  );
}
